import { useEffect, useState } from "react";
import Airtable from "airtable";
import { OPPORTUNITIES_TABLE } from "../utils/opportunityFilterFormulas.js";
import {
  buildCombinedInvoiceDocument,
  summarizeAccountsReceivable,
} from "../utils/accountsReceivable.js";

const apiKey = process.env.REACT_APP_AIR_KEY || process.env.REACT_APP_AIR_API_KEY;
const baseId = process.env.REACT_APP_AIR_BASEID;
const base =
  apiKey && baseId ? new Airtable({ apiKey }).base(baseId) : null;

function escapeRegex(value) {
  return String(value || "")
    .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    .replace(/'/g, "''");
}

function escapeFormulaValue(value) {
  return String(value || "").replace(/'/g, "\\'");
}

async function fetchScopedCompany(website, companyName) {
  const escapedWebsite = website ? escapeRegex(website) : "";
  const safeName = companyName ? String(companyName).replace(/'/g, "''") : "";
  const parts = [];
  if (escapedWebsite) {
    parts.push(`REGEX_MATCH({Domain}, '^${escapedWebsite}$')`);
    parts.push(`REGEX_MATCH({Domain}, '^www\\.${escapedWebsite}$')`);
  }
  if (safeName) parts.push(`{Name} = '${safeName}'`);
  if (!parts.length) return null;
  const companyFormula = parts.length > 1 ? `OR(${parts.join(", ")})` : parts[0];

  const records = await base("companies")
    .select({
      view: "Grid view",
      filterByFormula: companyFormula,
      maxRecords: 5,
    })
    .all();

  if (!records?.length) return null;
  const exactDomain = records.find(
    (c) =>
      c.fields?.Domain === website || c.fields?.Domain === `www.${website}`
  );
  if (companyName) {
    const exactName = records.find(
      (c) =>
        String(c.fields?.Name || "").toLowerCase() ===
        String(companyName).toLowerCase()
    );
    if (exactName) return exactName;
  }
  return exactDomain || records[0];
}

async function fetchCompanyOpportunities(company) {
  const companyId = escapeFormulaValue(company.id);
  const companyName = escapeFormulaValue(
    String(company.fields?.Name || "").trim()
  );
  const filterParts = [];
  if (companyId) filterParts.push(`{CompanyID} = '${companyId}'`);
  if (companyName) filterParts.push(`TRIM({CompanyName}) = '${companyName}'`);
  const filterByFormula =
    filterParts.length > 1
      ? `OR(${filterParts.join(", ")})`
      : filterParts[0] || "FALSE()";

  return base(OPPORTUNITIES_TABLE)
    .select({
      view: "Grid view",
      filterByFormula,
    })
    .all();
}

async function fetchCompanyPayments(companyId) {
  const res = await fetch(
    `/api/payments/list?companyId=${encodeURIComponent(
      companyId
    )}&unmatched=0&includeAllAllocated=1&limit=5000`
  );
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(payload.details || payload.error || "Failed to load payments");
  }
  return payload;
}

/**
 * Load the same combined statement document the AR explainer uses,
 * scoped by ?website= and ?company=.
 */
export default function useCompanyStatementDocument({ website, companyName }) {
  const [state, setState] = useState({
    loading: Boolean(website || companyName),
    error: null,
    company: null,
    doc: null,
  });

  useEffect(() => {
    const domain = String(website || "").trim();
    const name = String(companyName || "").trim();
    if (!domain && !name) {
      setState({ loading: false, error: null, company: null, doc: null });
      return undefined;
    }
    if (!base) {
      setState({
        loading: false,
        error: "Missing Airtable credentials",
        company: null,
        doc: null,
      });
      return undefined;
    }

    let cancelled = false;
    setState((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    const load = async () => {
      try {
        const company = await fetchScopedCompany(domain, name);
        if (cancelled) return;
        if (!company) {
          setState({
            loading: false,
            error: "Company not found",
            company: null,
            doc: null,
          });
          return;
        }

        const [opportunities, paymentsPayload] = await Promise.all([
          fetchCompanyOpportunities(company),
          fetchCompanyPayments(company.id).catch(() => ({ payments: [] })),
        ]);
        if (cancelled) return;

        const payments = paymentsPayload.payments || [];
        const summary = summarizeAccountsReceivable(opportunities || [], {
          companyFields: company.fields,
          companies: [company],
          payments,
        });
        const ledgerPaid =
          paymentsPayload.successFeeLedgerSumCAD != null
            ? Number(paymentsPayload.successFeeLedgerSumCAD)
            : paymentsPayload.ledgerSumCAD != null
              ? Number(paymentsPayload.ledgerSumCAD)
              : null;
        const paidToDate =
          ledgerPaid != null && Number.isFinite(ledgerPaid)
            ? ledgerPaid
            : company.fields?.PaidToDate != null &&
                company.fields?.PaidToDate !== ""
              ? Number(company.fields.PaidToDate) || 0
              : null;

        const doc =
          buildCombinedInvoiceDocument(summary.open || [], {
            companyFields: company.fields,
            ...(paidToDate != null ? { paidToDate } : {}),
            payments,
          }) || {
            seller: { name: "Upgraded" },
            billTo: {
              name: company.fields?.Name || name || "Client",
              website: company.fields?.Domain || domain || null,
            },
            dueToday: 0,
            dueLater: 0,
            balanceDue: 0,
            paidToDate: paidToDate || 0,
            lineItems: [],
            showDueToday: false,
          };

        setState({
          loading: false,
          error: null,
          company,
          doc,
        });
      } catch (err) {
        if (cancelled) return;
        console.error("[useCompanyStatementDocument]", err);
        setState({
          loading: false,
          error: err.message || "Failed to load statement",
          company: null,
          doc: null,
        });
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [website, companyName]);

  return state;
}
